import {
  JemConfiguration,
  getValidUrl,
  getRequest,
  ApiError,
  TenantResponseFromAPI,
  IUserProviderState,
  TenantRowDetails,
  postRequest
} from "@jem/components";

export async function getTenantDetails(
  configuration: JemConfiguration["GeneralLedgerApi"],
  getTokenFn: IUserProviderState["accessToken"]
): Promise<TenantResponseFromAPI[]> {
  const endpoint = `${configuration.baseApiUrl}${configuration.endpoints.getAllTenantDetails}`;

  const endpointUrl = getValidUrl(endpoint);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = await getRequest<any>(endpointUrl, getTokenFn);

  if (!response) {
    throw new ApiError("No response from server");
  }

  return response.map((res: TenantResponseFromAPI) => {
    const buildResp: TenantResponseFromAPI = res;
    buildResp["batchNamePrefix"] = res.miscConfig.batchNamePrefix;
    buildResp["allowNoReviewString"] = buildResp.allowNoReview ? "true" : "false";
    buildResp["overrideTenantSettingString"] = buildResp.overrideTenantSetting ? "true" : "false";
    return buildResp;
  });
}

export interface ITenantPanelResponse {
  success: boolean;
  errorMessage: string[];
}

// Coerce an arbitrary API response (including RFC 9110 problem-details bodies
// returned on 4xx/5xx, e.g. a 401 { title: "Unauthorized", status: 401 })
// into the ITenantPanelResponse shape expected by the UI.
function normalizeTenantPanelResponse(response: unknown): ITenantPanelResponse {
  if (!response || typeof response !== "object") {
    return { success: false, errorMessage: ["Something went wrong!!!"] };
  }

  const r = response as Partial<ITenantPanelResponse> & {
    title?: string;
    detail?: string;
    status?: number;
  };

  if (typeof r.success === "boolean") {
    return {
      success: r.success,
      errorMessage: Array.isArray(r.errorMessage) ? r.errorMessage : []
    };
  }

  // Problem-details / unexpected shape — synthesize a helpful message.
  const parts: string[] = [];
  if (r.status === 401) {
    parts.push("You are not authorized to perform this action.");
  } else if (r.status === 403) {
    parts.push("You do not have permission to perform this action.");
  }
  if (r.title) parts.push(r.title);
  if (r.detail) parts.push(r.detail);
  return {
    success: false,
    errorMessage: parts.length > 0 ? parts : ["Something went wrong!!!"]
  };
}

export async function onBoardNewTenantDetails(
  configuration: JemConfiguration["GeneralLedgerApi"],
  getTokenFn: IUserProviderState["accessToken"],
  data: TenantRowDetails
): Promise<ITenantPanelResponse> {
  const endpoint = `${configuration.baseApiUrl}${configuration.endpoints.onboardTenantDetails}`;
  const endpointUrl = getValidUrl(endpoint);

  const payload: TenantRowDetails = data;
  const response = await postRequest<ITenantPanelResponse>(endpointUrl, payload, getTokenFn);

  return normalizeTenantPanelResponse(response);
}

export async function updateTenantDetails(
  configuration: JemConfiguration["GeneralLedgerApi"],
  getTokenFn: IUserProviderState["accessToken"],
  data: TenantRowDetails
): Promise<ITenantPanelResponse> {
  const endpoint = `${configuration.baseApiUrl}${configuration.endpoints.updateTenantDetails}`;
  const endpointUrl = getValidUrl(endpoint);

  const payload: TenantRowDetails = data;
  const response = await postRequest<ITenantPanelResponse>(endpointUrl, payload, getTokenFn);

  return normalizeTenantPanelResponse(response);
}
